import { ContextStore } from 'bernie-core';
import { getBexApiContext } from 'bernie-graphql';

import { ChildModel } from 'stores/models/child-model';
import { getSearchIntentInput } from 'stores/adapters/bex-api/search/customer-interest-adapter';
import { getShoppingContextInput } from 'stores/adapters/bex-api/search/shopping-context-adapter';
import { RoomModel } from 'stores/models/room-model';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { CustomerInterest } from 'models/common/customer-interest';
import { MarketingChannels } from 'constants/marketing-channels';
import { MarketingPrices } from 'constants/marketing-prices';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

import {
  ChildInput,
  CoordinatesInput,
  DateInput,
  DestinationInput,
  PropertyDateRangeInput,
  PropertyMarketingInfoInput,
  PropertySearch,
  PropertySearchOptions,
  PropertySort,
  RoomInput
} from '__generated__/api';
import { getFiltersInput } from './filters';
import { getLegacyCriteria } from './legacy-input/legacy-search-criteria';

export const getPropertySearchInput = (
  context: ContextStore,
  customerInterests: CustomerInterest[],
  searchCriteria: SearchCriteriaModel,
  sortAndFilterOptions: any,
  multiItemContext: MultiItemContext
): PropertySearch.Variables => ({
  context: getBexApiContext(context),
  destination: getDestinationInput(searchCriteria),
  filters: getFiltersInput(searchCriteria, sortAndFilterOptions),
  legacyCriteria: getLegacyCriteria(searchCriteria, context),
  marketing: getMarketingInfoInput(searchCriteria),
  rooms: getRooms(searchCriteria),
  searchIntent: getSearchIntentInput(customerInterests, searchCriteria),
  searchOptions: searchCriteria.useRewards === 'true' ? [PropertySearchOptions.SHOP_WITH_POINTS] : null,
  shoppingContext: getShoppingContextInput(multiItemContext),
  sort: getSortInput(searchCriteria),
  dateRange: getDateRangeInput(searchCriteria)
});

const getRooms = (searchCriteria: SearchCriteriaModel): RoomInput[] | null => {
  if (searchCriteria.rooms) {
    return searchCriteria.rooms.map(getRoomInput);
  }

  return null;
};

const getDateRangeInput = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  const checkInDate = searchCriteria.startDate ? parseDateString(searchCriteria.startDate) : null;
  const checkOutDate = searchCriteria.endDate ? parseDateString(searchCriteria.endDate) : null;

  if (!checkInDate || !checkOutDate) {
    return generateFutureDates(searchCriteria);
  }

  return {
    checkInDate,
    checkOutDate
  };
};

export const parseDateString = (dateString: string): DateInput | null => {
  const dateParts = dateString.split('-') || [];

  if (!dateParts || dateParts.length !== 3) {
    return null;
  }

  return {
    day: Number.parseInt(dateParts[2]),
    month: Number.parseInt(dateParts[1]),
    year: Number.parseInt(dateParts[0])
  };
};

const getDestinationInput = (searchCriteria: SearchCriteriaModel): DestinationInput => {
  const coordinates: CoordinatesInput | null = searchCriteria.latLong ? getCoordinates(searchCriteria.latLong) : null;

  return {
    regionName: searchCriteria.destination || null,
    regionId: searchCriteria.regionId || null,
    coordinates,
    pinnedPropertyId: searchCriteria.selected || null,
    propertyIds: getPropertyIds(searchCriteria)
  };
};

const getCoordinates = (latLong: string): CoordinatesInput | null => {
  const latLongParts = latLong.split(',') || [];

  if (!latLongParts || latLongParts.length !== 2 || !latLongParts[0] || !latLongParts[0]) {
    return null;
  }

  return {
    latitude: Number.parseFloat(latLongParts[0]),
    longitude: Number.parseFloat(latLongParts[1])
  };
};

const getRoomInput = (roomModel: RoomModel): RoomInput => ({
  adults: roomModel.adults,
  children: roomModel.children.map(getChildInput)
});

const getChildInput = (childModel: ChildModel): ChildInput => ({
  age: childModel.age
});

const getSortInput = (searchCriteria: SearchCriteriaModel): PropertySort | null => {
  const result = PropertySort[PropertySort[searchCriteria.sort]];

  return result ? result : null;
};

const getPropertyIds = (searchCriteria: SearchCriteriaModel): string[] | null => {
  if (searchCriteria.group && searchCriteria.group.length > 0) {
    return searchCriteria.group.split(',');
  }

  return null;
};

const generateFutureDates = (searchCriteria: SearchCriteriaModel): PropertyDateRangeInput | null => {
  if (!searchCriteria.daysInFuture || !searchCriteria.stayLength) {
    return null;
  }

  const checkInDate = new Date();
  checkInDate.setDate(checkInDate.getDate() + parseInt(searchCriteria.daysInFuture, 10));
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkInDate.getDate() + parseInt(searchCriteria.stayLength, 10));

  return {
    checkInDate: parseDateString(checkInDate.toISOString().split('T')[0]),
    checkOutDate: parseDateString(checkOutDate.toISOString().split('T')[0])
  };
};

const getMarketingInfoInput = (searchCriteria: any): PropertyMarketingInfoInput | null => {
  const channels = [];
  const prices = [];

  Object.keys(searchCriteria).forEach(k => {
    if (k.toLowerCase() in MarketingChannels && searchCriteria[k]) {
      channels.push({ key: k, value: searchCriteria[k] });
    } else if (k.toLowerCase() in MarketingPrices && searchCriteria[k]) {
      prices.push({ key: k, value: searchCriteria[k] });
    }
  });

  return { marketingChannels: channels, marketingPrices: prices };
};
